import { Extension, type Editor, type Range } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import { ReactRenderer } from '@tiptap/react'
import Suggestion from '@tiptap/suggestion'
import {
    Heading1,
    Heading2,
    Heading3,
    Image as ImageIcon,
    List,
    ListOrdered,
    Minus,
    MousePointerClick,
    MoveVertical,
    Pilcrow,
    Quote,
    type LucideIcon,
} from 'lucide-react'
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import tippy, { type GetReferenceClientRect, type Instance } from 'tippy.js'

import { cn } from '@/lib/utils'

import { summonImagePicker } from './image-picker-storage'

/* ----------------------------------------------------------------------------
   Type model (Maily-inspired, but sharper: commands are flat here — no sub-menu
   cognitive load for beginner merchants. Complex compositions live in the
   "Start from template" gallery instead, which is the right place for them.)
---------------------------------------------------------------------------- */

export interface SlashCommand {
    id: string
    title: string
    description: string
    keywords: string[]
    icon: LucideIcon
    /** Quick one-line hint shown on hover */
    shortcut?: string
    run: (opts: { editor: Editor; range: Range }) => void
}

export interface SlashGroup {
    id: string
    title: string
    commands: SlashCommand[]
}

/* ----------------------------------------------------------------------------
   Default Tagada-tailored catalog
---------------------------------------------------------------------------- */

export const DEFAULT_SLASH_GROUPS: SlashGroup[] = [
    {
        id: 'text',
        title: 'Text',
        commands: [
            {
                id: 'paragraph',
                title: 'Text',
                description: 'Plain paragraph',
                keywords: ['text', 'paragraph', 'body', 'p'],
                icon: Pilcrow,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .setParagraph()
                        .run(),
            },
            {
                id: 'h1',
                title: 'Heading 1',
                description: 'Big section title',
                keywords: ['heading', 'title', 'h1', 'big'],
                icon: Heading1,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .setHeading({ level: 1 })
                        .run(),
            },
            {
                id: 'h2',
                title: 'Heading 2',
                description: 'Medium section title',
                keywords: ['heading', 'h2', 'subtitle'],
                icon: Heading2,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .setHeading({ level: 2 })
                        .run(),
            },
            {
                id: 'h3',
                title: 'Heading 3',
                description: 'Small title',
                keywords: ['heading', 'h3'],
                icon: Heading3,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .setHeading({ level: 3 })
                        .run(),
            },
            {
                id: 'bullet-list',
                title: 'Bullet list',
                description: 'Unordered list',
                keywords: ['list', 'bullet', 'ul', 'unordered'],
                icon: List,
                run: ({ editor, range }) =>
                    editor.chain().focus().deleteRange(range).toggleBulletList().run(),
            },
            {
                id: 'ordered-list',
                title: 'Numbered list',
                description: 'Ordered list',
                keywords: ['list', 'ordered', 'numbered', 'ol'],
                icon: ListOrdered,
                run: ({ editor, range }) =>
                    editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
            },
            {
                id: 'quote',
                title: 'Quote',
                description: 'Callout / pull quote',
                keywords: ['quote', 'blockquote', 'callout'],
                icon: Quote,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .toggleBlockquote()
                        .run(),
            },
        ],
    },
    {
        id: 'blocks',
        title: 'Email blocks',
        commands: [
            {
                id: 'button',
                title: 'Button',
                description: 'Call-to-action button',
                keywords: ['button', 'cta', 'link', 'action'],
                icon: MousePointerClick,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .insertButton()
                        .run(),
            },
            {
                id: 'image',
                title: 'Image',
                description: 'Pick from media library',
                keywords: ['image', 'picture', 'photo', 'img', 'media', 'library'],
                icon: ImageIcon,
                run: ({ editor, range }) => {
                    // Drop the slash query first so the cursor is in the right place
                    editor.chain().focus().deleteRange(range).run()
                    summonImagePicker(editor, (url) => {
                        editor
                            .chain()
                            .focus()
                            .setImage({ src: url })
                            .run()
                    })
                },
            },
            {
                id: 'divider',
                title: 'Divider',
                description: 'Horizontal line',
                keywords: ['divider', 'separator', 'hr', 'rule', 'line'],
                icon: Minus,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .setHorizontalRule()
                        .run(),
            },
            {
                id: 'spacer',
                title: 'Spacer',
                description: 'Vertical breathing room',
                keywords: ['spacer', 'space', 'gap', 'margin', 'padding'],
                icon: MoveVertical,
                run: ({ editor, range }) =>
                    editor
                        .chain()
                        .focus()
                        .deleteRange(range)
                        .insertSpacer()
                        .run(),
            },
        ],
    },
]

/* ----------------------------------------------------------------------------
   Extension
---------------------------------------------------------------------------- */

const PluginKeyInstance = new PluginKey('emailSlashCommand')

export interface SlashCommandOptions {
    groups: SlashGroup[]
}

export const SlashCommandExtension = Extension.create<SlashCommandOptions>({
    name: 'emailSlashCommand',

    addOptions() {
        return {
            groups: DEFAULT_SLASH_GROUPS,
        }
    },

    addProseMirrorPlugins() {
        return [
            Suggestion<SlashCommand, SlashCommand>({
                editor: this.editor,
                pluginKey: PluginKeyInstance,
                char: '/',
                startOfLine: false,
                allowSpaces: false,
                command: ({ editor, range, props }) => {
                    props.run({ editor, range })
                },
                items: ({ query }) => {
                    if (!query) {
                        return this.options.groups.flatMap((g) => g.commands).slice(0, 14)
                    }
                    const q = query.toLowerCase()
                    return this.options.groups
                        .flatMap((g) => g.commands)
                        .filter((c) => {
                            const hay = `${c.title} ${c.description} ${c.keywords.join(' ')}`.toLowerCase()
                            return hay.includes(q)
                        })
                        .slice(0, 14)
                },
                render: () => {
                    let component: ReactRenderer<SlashListHandle> | null = null
                    let popup: Instance[] | null = null
                    const groups = this.options.groups

                    return {
                        onStart: (props) => {
                            const rendererProps: SlashListProps = {
                                items: props.items,
                                command: (c) => props.command(c),
                                groups,
                                query: props.query || '',
                            }
                            component = new ReactRenderer(SlashCommandList, {
                                props: rendererProps,
                                editor: props.editor,
                            })
                            if (!props.clientRect) return
                            popup = tippy('body', {
                                getReferenceClientRect:
                                    props.clientRect as GetReferenceClientRect,
                                appendTo: () => document.body,
                                content: component.element,
                                showOnCreate: true,
                                interactive: true,
                                trigger: 'manual',
                                placement: 'bottom-start',
                            })
                        },
                        onUpdate: (props) => {
                            component?.updateProps({
                                items: props.items,
                                query: props.query || '',
                            })
                            const instance = popup?.[0]
                            if (!instance || instance.state.isDestroyed) return
                            if (!props.clientRect) return
                            instance.setProps({
                                getReferenceClientRect:
                                    props.clientRect as GetReferenceClientRect,
                            })
                        },
                        onKeyDown: (props) => {
                            if (props.event.key === 'Escape') {
                                popup?.[0]?.destroy()
                                component?.destroy()
                                return true
                            }
                            return component?.ref?.onKeyDown(props.event) ?? false
                        },
                        onExit: () => {
                            popup?.[0]?.destroy()
                            component?.destroy()
                            popup = null
                            component = null
                        },
                    }
                },
            }),
        ]
    },
})

/* ----------------------------------------------------------------------------
   Command list popup
---------------------------------------------------------------------------- */

type SlashListHandle = { onKeyDown: (e: KeyboardEvent) => boolean }

interface SlashListProps {
    items: SlashCommand[]
    command: (c: SlashCommand) => void
    groups: SlashGroup[]
    query: string
}

const SlashCommandList = forwardRef<SlashListHandle, SlashListProps>(
    function SlashCommandList({ items, command, groups, query }, ref) {
        const [selected, setSelected] = useState(0)
        const listRef = useRef<HTMLDivElement>(null)

        useEffect(() => setSelected(0), [items])

        useEffect(() => {
            const el = listRef.current?.querySelector<HTMLElement>(
                `[data-index="${selected}"]`,
            )
            el?.scrollIntoView({ block: 'nearest' })
        }, [selected])

        useImperativeHandle(ref, () => ({
            onKeyDown: (event) => {
                if (event.key === 'ArrowUp') {
                    setSelected((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length))
                    return true
                }
                if (event.key === 'ArrowDown') {
                    setSelected((i) => (items.length === 0 ? 0 : (i + 1) % items.length))
                    return true
                }
                if (event.key === 'Enter' || event.key === 'Tab') {
                    const item = items[selected]
                    if (item) {
                        command(item)
                        return true
                    }
                }
                return false
            },
        }))

        if (items.length === 0) {
            return (
                <div className="w-72 rounded-lg border bg-popover p-3 text-sm text-muted-foreground shadow-md">
                    No blocks match "{query}". Try: button, image, list, spacer.
                </div>
            )
        }

        // Re-group filtered items by their source group for nice labels
        const byGroup = new Map<string, SlashCommand[]>()
        for (const cmd of items) {
            const group = groups.find((g) =>
                g.commands.some((c) => c.id === cmd.id),
            )
            const gid = group?.id || 'other'
            const list = byGroup.get(gid) || []
            list.push(cmd)
            byGroup.set(gid, list)
        }

        let flat = -1

        return (
            <div
                ref={listRef}
                className="w-80 overflow-hidden rounded-lg border bg-popover text-sm shadow-md"
            >
                <div className="max-h-80 overflow-y-auto p-1">
                    {Array.from(byGroup.entries()).map(([gid, list]) => {
                        const group = groups.find((g) => g.id === gid)
                        return (
                            <div key={gid} className="mb-1">
                                <div className="px-2 pb-0.5 pt-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                                    {group?.title || gid}
                                </div>
                                {list.map((cmd) => {
                                    flat += 1
                                    const isSelected = flat === selected
                                    const Icon = cmd.icon
                                    const index = flat
                                    return (
                                        <button
                                            key={cmd.id}
                                            type="button"
                                            data-index={index}
                                            onMouseEnter={() => setSelected(index)}
                                            onClick={() => command(cmd)}
                                            className={cn(
                                                'flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left',
                                                isSelected && 'bg-muted',
                                            )}
                                        >
                                            <div className="flex h-8 w-8 items-center justify-center rounded-md border bg-background">
                                                <Icon className="h-4 w-4" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-medium">
                                                    {cmd.title}
                                                </div>
                                                <div className="truncate text-[11px] text-muted-foreground">
                                                    {cmd.description}
                                                </div>
                                            </div>
                                        </button>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
                <div className="flex items-center justify-between border-t bg-muted/20 px-2 py-1 text-[10px] text-muted-foreground">
                    <span>
                        <kbd className="rounded border bg-background px-1">↑↓</kbd>{' '}
                        navigate
                    </span>
                    <span>
                        <kbd className="rounded border bg-background px-1">Enter</kbd>{' '}
                        insert
                    </span>
                </div>
            </div>
        )
    },
)
